import { Box, Input, Select, getVariant } from '@apia/theme';
import { TApiaFilter, TApiaFilterValue } from '../../../../../types';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { useRef } from 'react';
import { useIntermediateValue, usePrevious } from '@apia/util';
import { ApiaUtil } from '@apia/components';

const units = ['B', 'KB', 'MB', 'GB'];

const NoMemoRangeFilter = ({
  filter,
  className,
}: {
  filter: TApiaFilter;
  className?: string;
}) => {
  const { onFilterBlur, onFilterChange, onFilterPressEnter } =
    useResponsiveTableContext();
  const filterLow: TApiaFilter = {
    ...filter,
    changeFiltersTimestamp: filter.changeFiltersTimestamp,
    currentValue: filter.currentValue,
    deleteFiltersTimestamp: filter.deleteFiltersTimestamp,
    id: filter.id,
    toolTip: filter.toolTip,
    type: 'apiaNumber',
  };

  const multiplier = useRef(1);
  const lastEmittedHigh = useRef<TApiaFilterValue | null>(null);
  const lastEmittedLow = useRef<TApiaFilterValue | null>(null);
  const lastEmittedMultiplier = useRef(1);

  const previousChangeTimestamp = usePrevious(filter.changeFiltersTimestamp);
  const previousDeleteTimestamp = usePrevious(filter.deleteFiltersTimestamp);

  if (previousChangeTimestamp.current !== filter.changeFiltersTimestamp) {
    lastEmittedLow.current = filterLow.currentValue;
    lastEmittedHigh.current = filterLow.filterToValue || '';
  }
  if (previousDeleteTimestamp.current !== filter.deleteFiltersTimestamp) {
    lastEmittedLow.current = '';
    lastEmittedHigh.current = '';
  }

  const emitLow = (
    method: ((ev: TApiaFilter) => unknown) | undefined,
    currentValue: TApiaFilterValue,
  ) => {
    const numberValue = currentValue ?? '';

    if (
      lastEmittedLow.current === numberValue &&
      lastEmittedMultiplier.current === multiplier.current
    ) {
      return;
    }

    lastEmittedLow.current = numberValue;
    lastEmittedMultiplier.current = multiplier.current;
    const emitValue = numberValue;

    void method?.({
      ...filterLow,
      currentValue: emitValue,
      filterToValue: lastEmittedHigh.current ?? '',
      sizeMultiplier: multiplier.current,
    });
    setLowValue(emitValue);
  };
  const emitHigh = (
    method: ((ev: TApiaFilter) => unknown) | undefined,
    currentValue: TApiaFilterValue,
  ) => {
    const numberValue = currentValue ?? '';

    if (
      lastEmittedHigh.current === numberValue &&
      lastEmittedMultiplier.current === multiplier.current
    ) {
      return;
    }

    lastEmittedHigh.current = numberValue;
    lastEmittedMultiplier.current = multiplier.current;
    const emitValue = numberValue;

    void method?.({
      ...filterLow,
      currentValue: lastEmittedLow.current ?? '',
      filterToValue: emitValue,
      sizeMultiplier: multiplier.current,
    });
    setHighValue(emitValue);
  };

  const [lowValue, setLowValue] = useIntermediateValue(filterLow.currentValue);
  const [highValue, setHighValue] = useIntermediateValue(
    filterLow.filterToValue,
  );
  const [unit, setUnit] = useIntermediateValue(filterLow.sizeMultiplier);

  return (
    <Box
      className={`ApiaFilter__Range ${className || ''}`}
      {...getVariant('layout.common.filters.rangeFilter')}
    >
      <Box className="ApiaFilter__Range__Low">
        <Input
          onChange={({ target: { value: currentValue } }) => {
            setLowValue(currentValue);
            emitLow(onFilterChange, currentValue);
          }}
          onBlur={({ target: { value: currentValue } }) => {
            if (currentValue) {
              const newValue =
                ApiaUtil.instance.parsers.apiaNumber(currentValue);

              emitLow(onFilterBlur, newValue);
            } else {
              emitLow(onFilterBlur, currentValue);
            }
          }}
          onFocus={(ev) => {
            ev.target.setSelectionRange(0, ev.target.value.length);
          }}
          onKeyDown={({ code, target }) => {
            if (code === 'Enter') {
              const newValue = ApiaUtil.instance.parsers.apiaNumber(
                (target as HTMLInputElement).value,
              );
              emitLow(onFilterPressEnter, newValue);
            }
          }}
          value={lowValue}
        />
      </Box>
      {' - '}
      <Box className="ApiaFilter__Range__High">
        <Input
          onChange={({ target: { value: currentValue } }) => {
            setHighValue(currentValue);
            emitHigh(onFilterChange, currentValue);
          }}
          onBlur={({ target: { value: currentValue } }) => {
            if (currentValue) {
              const newValue =
                ApiaUtil.instance.parsers.apiaNumber(currentValue);
              emitHigh(onFilterBlur, newValue);
            } else {
              emitHigh(onFilterBlur, currentValue);
            }
          }}
          onFocus={(ev) => {
            ev.target.setSelectionRange(0, ev.target.value.length);
          }}
          onKeyDown={({ code, target }) => {
            if (code === 'Enter') {
              const newValue = ApiaUtil.instance.parsers.apiaNumber(
                (target as HTMLInputElement).value,
              );
              emitHigh(onFilterPressEnter, newValue);
            }
          }}
          value={highValue}
        />
      </Box>
      {filter.isSize && (
        <Select
          className="ApiaFilter__Range__UnitSelector"
          onChange={(ev) => {
            const unit = ev.target.value;
            const index = units.indexOf(unit);
            if (index !== -1) {
              multiplier.current = 2 ** (index * 10);
              setUnit(multiplier.current);
              /**
               * It's enough to fire event on low because it updates the multiplier in the original filter
               */
              emitLow(onFilterChange, lastEmittedLow.current ?? '');
            }
          }}
          value={units[Math.log2(unit || 1) / 10]}
        >
          {units.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      )}
    </Box>
  );
};

export const RangeFilter = NoMemoRangeFilter;
